import { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { Resolver, useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  addressInfoRequest,
  useProductName,
  useraccountDetailsRequest,
  authorizedUserOptionsRequest,
  resetUpdateAuthorizedUser,
  updateAuthorizedUserRequest,
  contactInfoRequest,
  renderDescriptionParagraphs,
} from '@rb-serve-app/core';
import Breadcrumbs from './BreadCrumbs';
import { useNavigate } from 'react-router-dom';
import { BalanceIcon } from '../assets/svg/StatusIcon';
import CommanModal from './CommanModal';
import Spinner from './Spinner';
import { useRecaptchaToken } from '@rb-serve-app/core';
import { logBrazeCustomEvent } from '../config/braze';

interface AddCardHolderFormType {
  relationshipToPrimary: string;
  salutation: string;
  firstName: string;
  middleInitial: string | null;
  lastName: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  country: string;
  province: string;
  postalCode: string;
  dateOfBirth: string;
  home: string | null;
  business: string | null;
  mobile: string;
  other: string | null;
}

const AddCardHolderForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getRecaptchaToken = useRecaptchaToken();
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );
  const { accountId, customerId } = useSelector((state: any) => state.user);
  const { contactDetails, loading } = useSelector(
    (state: any) => state.contactInfo
  );
  const {
    error,
    loadingAuthUser,
    authorizedUserOptions,
    authorizedUser,
    errorOptions,
  } = useSelector((state: any) => state.supplemeantry);
  const { addressDetails } = useSelector((state: any) => state.contactInfo);
  const { productName } = useProductName();
  const { entries, assets } = contentfulData;
  const { rb_supplementaryCards, rb_links, rb_error } = entries;
  const provinceCodeMap: Record<string, string> = {
    ALBERTA: 'AB',
    BRITISH_COLUMBIA: 'BC',
    MANITOBA: 'MB',
    NEW_BRUNSWICK: 'NB',
    NEWFOUNDLAND_AND_LABRADOR: 'NL',
    NORTHWEST_TERRITORIES: 'NT',
    NOVA_SCOTIA: 'NS',
    NUNAVUT: 'NU',
    ONTARIO: 'ON',
    PRINCE_EDWARD_ISLAND: 'PE',
    QUEBEC: 'QC',
    SASKATCHEWAN: 'SK',
    YUKON: 'YT',
  };

  const schema = yup.object({
    relationshipToPrimary: yup
      .string()
      .required(
        rb_supplementaryCards?.addSupplementaryCardDetails?.mandatory_field
      ),
    salutation: yup
      .string()
      .required(
        rb_supplementaryCards?.addSupplementaryCardDetails?.mandatory_field
      ),
    firstName: yup
      .string()
      .required(
        rb_supplementaryCards?.addSupplementaryCardDetails?.mandatory_field
      )
      .matches(
        /^[a-zA-Z0-9/\-,'"&() \u00A7\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u017F]+$/,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.invalidEntry
      )
      .max(
        21,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.exceed21
      ),
    lastName: yup
      .string()
      .required(
        rb_supplementaryCards?.addSupplementaryCardDetails?.mandatory_field
      )
      .matches(
        /^[a-zA-Z0-9/\-,'"&() \u00A7\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u017F]+$/,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.invalidEntry
      )
      .max(
        21,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.exceed21
      ),
    middleInitial: yup
      .string()
      .nullable()
      .notRequired()
      .max(
        21,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.exceed21
      )
      .matches(
        /^[a-zA-Z0-9/\-,'"&() \u00A7\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u017F]*$/,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.invalidEntry
      ),
    addressLine1: yup
      .string()
      .required(
        rb_supplementaryCards?.addSupplementaryCardDetails?.mandatory_field
      )
      .max(
        40,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.exceed40
      )
      .matches(
        /^([a-zA-Z0-9-`~!@#$%^&*()_+=,/'. \u0080-\u024F]+(?:. |-| ^([a-zA-Z0-9-`~!@#$%^&*()_+=,/'. \u0080-\u024F]+(?:. |-| |'))*[a-zA-Z0-9-`~!@#$%^&*()_+=,/'. \u0080-\u024F]*$|'))*[a-zA-Z0-9-`~!@#$%^&*()_+=,/'. \u0080-\u024F]*$/,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.invalidEntry
      ),
    addressLine2: yup
      .string()
      .nullable()
      .max(
        40,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.exceed40
      )
      .matches(
        /^([a-zA-Z0-9-`~!@#$%^&*()_+=,/'. \u0080-\u024F]+(?:. |-| ^([a-zA-Z0-9-`~!@#$%^&*()_+=,/'. \u0080-\u024F]+(?:. |-| |'))*[a-zA-Z0-9-`~!@#$%^&*()_+=,/'. \u0080-\u024F]*$|'))*[a-zA-Z0-9-`~!@#$%^&*()_+=,/'. \u0080-\u024F]*$/,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.invalidEntry
      )
      .notRequired(),
    city: yup
      .string()
      .required(
        rb_supplementaryCards?.addSupplementaryCardDetails?.mandatory_field
      )
      .max(
        19,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.exceed19
      )
      .matches(
        /^([a-zA-Z0-9-`~!@#$%^&*()_+=,/'. \u0080-\u024F]+(?:. |-| ^([a-zA-Z0-9-`~!@#$%^&*()_+=,/'. \u0080-\u024F]+(?:. |-| |'))*[a-zA-Z0-9-`~!@#$%^&*()_+=,/'. \u0080-\u024F]*$|'))*[a-zA-Z0-9-`~!@#$%^&*()_+=,/'. \u0080-\u024F]*$/,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.invalidEntry
      ),
    province: yup
      .string()
      .required(
        rb_supplementaryCards?.addSupplementaryCardDetails?.mandatory_field
      ),
    postalCode: yup
      .string()
      .required(
        rb_supplementaryCards?.addSupplementaryCardDetails?.mandatory_field
      )
      .matches(
        /^([A-Za-z]\d[A-Za-z])\s?(\d[A-Za-z]\d)$/,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.postalCode
      )
      .max(
        7,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.exceed7
      ),
    dateOfBirth: yup
      .string()
      .required(
        rb_supplementaryCards?.addSupplementaryCardDetails?.mandatory_field
      )
      .test(
        'notFuture',
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors
          ?.invalidEntry,
        (value) => {
          if (!value) return true;
          const [month, day, year] = value.split('/');
          const dob = new Date(`${year}-${month}-${day}`);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return dob <= today;
        }
      )
      .matches(
        /^(0[1-9]|1[0-2])\/([0-2][0-9]|3[01])\/\d{4}$/,
        rb_supplementaryCards?.addSupplementaryCardDetails?.inline_errors?.dob
      ),
    home: yup
      .string()
      .nullable()
      .notRequired()
      .matches(/^[0-9]{10}$/, { excludeEmptyString: true }),
    business: yup
      .string()
      .nullable()
      .notRequired()
      .matches(/^[0-9]{10}$/, { excludeEmptyString: true }),
    mobile: yup
      .string()
      .required(
        rb_supplementaryCards?.addSupplementaryCardDetails?.mandatory_field
      )
      .matches(
        /^[0-9]{10}$/,
        rb_supplementaryCards?.addSupplementaryCardDetails?.mandatory_field
      ),
    other: yup
      .string()
      .nullable()
      .notRequired()
      .matches(/^[0-9]{10}$/, { excludeEmptyString: true }),
  });

  const phoneFields: ('mobile' | 'business' | 'home' | 'other')[] = [
    'mobile',
    'business',
    'home',
    'other',
  ];
  const phoneLabels: Record<'mobile' | 'business' | 'home' | 'other', string> =
    {
      mobile:
        rb_supplementaryCards?.addSupplementaryCardDetails?.labels?.mobile,
      business:
        rb_supplementaryCards?.addSupplementaryCardDetails?.labels?.business,
      home: rb_supplementaryCards?.addSupplementaryCardDetails?.labels?.home,
      other: rb_supplementaryCards?.addSupplementaryCardDetails?.labels?.other,
    };
  const phonePlaceholders: Record<
    'mobile' | 'business' | 'home' | 'other',
    string
  > = {
    mobile:
      rb_supplementaryCards?.addSupplementaryCardDetails?.placeHolders?.mobile,
    business:
      rb_supplementaryCards?.addSupplementaryCardDetails?.placeHolders
        ?.business,
    home: rb_supplementaryCards?.addSupplementaryCardDetails?.placeHolders
      ?.home,
    other:
      rb_supplementaryCards?.addSupplementaryCardDetails?.placeHolders?.other,
  };

  const [phoneNumbers, setPhoneNumbers] = useState<
    Record<'mobile' | 'business' | 'home' | 'other', string>
  >({
    mobile: '',
    business: '',
    home: '',
    other: '',
  });
  const [dob, setDob] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState<string | null>(
    null
  );
  const [isSuccessFailed, setIsSuccessFailed] = useState(false);
  const [failedModalOpen, setFailedModalOpen] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState<{
    title: string;
    description: string;
  } | null>(null);
  const [isRecaptchaModalOpen, setIsRecaptchaModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    setValue,
    control,
    trigger,
  } = useForm<AddCardHolderFormType>({
    mode: 'onChange',
    resolver: yupResolver(schema) as unknown as Resolver<AddCardHolderFormType>,
    defaultValues: {
      relationshipToPrimary: '',
      salutation: '',
      firstName: '',
      middleInitial: null,
      lastName: '',
      addressLine1: '',
      addressLine2: null,
      city: '',
      province: '',
      postalCode: '',
      dateOfBirth: '',
      home: null,
      business: null,
      mobile: '',
      other: null,
    },
  });

  const handlePhoneChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: 'mobile' | 'business' | 'home' | 'other'
  ) => {
    const value = e.target.value;
    if (!value) {
      setPhoneNumbers((prevState) => ({
        ...prevState,
        [field]: '',
      }));
      setValue(field, '');
      return;
    }
    const rawValue = value.replace(/\D/g, '').slice(0, 10);
    let formattedValue = rawValue;
    if (rawValue.length <= 3) {
      formattedValue = `(${rawValue}`;
    } else if (rawValue.length <= 6) {
      formattedValue = `(${rawValue.slice(0, 3)}) ${rawValue.slice(3)}`;
    } else {
      formattedValue = `(${rawValue.slice(0, 3)}) ${rawValue.slice(3, 6)}-${rawValue.slice(6, 10)}`;
    }
    setPhoneNumbers((prevState) => ({
      ...prevState,
      [field]: formattedValue,
    }));
    setValue(field, rawValue);
    trigger(field);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length === 8) {
      value =
        value.slice(0, 2) + '/' + value.slice(2, 4) + '/' + value.slice(4);
    }
    setDob(value);
    setValue('dateOfBirth', value, { shouldValidate: true });
  };

  const convertDateFormat = (date: any) => {
    const [month, day, year] = date.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  useEffect(() => {
    if (accountId && customerId) {
      if (contactDetails === null && !loading) {
        dispatch(
          contactInfoRequest({
            accountId: accountId,
            primaryCustomerId: customerId,
            customerId: customerId,
          })
        );
      }
    }
    dispatch(authorizedUserOptionsRequest({ accountId, customerId }));
    //dispatch(useraccountDetailsRequest({ accountId, customerId }));
    dispatch(addressInfoRequest({ accountId, customerId }));
  }, [accountId, customerId]);

  useEffect(() => {
    logBrazeCustomEvent('viewed_add_new_cardholder_details');
  }, []);

  useEffect(() => {
    if (authorizedUser?.status === 'APPROVED') {
      setConfirmationNumber(authorizedUser?.confirmationNumber);
      setIsSuccessModalOpen(true);
    }
    if (error || errorOptions) {
      setIsSuccessFailed(true);
      setFailedModalOpen(true);
    }
  }, [authorizedUser, error, errorOptions]);

  useEffect(() => {
    if (error?.status === '401') {
      setRecaptchaError({
        title: entries?.rb_error?.recaptchaErrors[error?.errorCode]?.title,
        description:
          entries?.rb_error?.recaptchaErrors[error?.errorCode]?.description,
      });
      setIsRecaptchaModalOpen(true);
    } else {
      if (error) {
        setRecaptchaError({
          title: entries?.rb_error?.genericApiFailure?.header,
          description: entries?.rb_error?.genericApiFailure?.text,
        });
        setIsRecaptchaModalOpen(true);
      }
    }
  }, [error]);

  const onSubmit = async (data: AddCardHolderFormType) => {
    logBrazeCustomEvent('action_added_new_cardholder');

    try {
      const token = await getRecaptchaToken('add_supplementary_card');
      const formattedDateOfBirth = convertDateFormat(data.dateOfBirth);

      // Find COUNTRY config
      const country =
        authorizedUserOptions?.configurations?.find(
          (config: { name: string }) => config.name === 'COUNTRY'
        )?.validOptions?.[0] || 'CAN';

      // Find STATE_PROVINCE config for the selected country
      const stateProvinceConfig = authorizedUserOptions?.configurations?.find(
        (config: { name: string }) => config.name === 'STATE_PROVINCE'
      )?.validOptions;

      let stateProvince = data.province;

      if (stateProvinceConfig && country === 'CAN') {
        const canadaConfig = stateProvinceConfig.find((opt: string) =>
          opt.startsWith('CAN=')
        );

        if (canadaConfig) {
          const provinces = canadaConfig
            .replace('CAN=[', '')
            .replace(']', '')
            .split(',')
            .map((s: any) => s.trim());
          const selected = stateProvince?.trim().toUpperCase();
          // Only keep if user's selection is valid
          if (!provinces.includes(selected)) {
            stateProvince = provinces[0]; // fallback to first valid one
          }
        }
      }

      const requestBody = {
        relationshipToCustomer: data.relationshipToPrimary,
        lastName: data.lastName,
        country,
        city: data.city,
        prefix: data.salutation,
        postalCode: data.postalCode,
        alternatePhone1: data.mobile,
        alternatePhone2: data.other,
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2,
        homePhone: data.home,
        stateProvince,
        dateOfBirth: formattedDateOfBirth,
        assignmentId: authorizedUserOptions?.assignmentId ?? '',
        firstName: data.firstName,
        middleName: data.middleInitial,
        businessPhone: data.business,
        recaptchaToken: token,
      };
      // Remove empty values ("") from requestBody
      const filteredRequestBody = Object.fromEntries(
        Object.entries(requestBody).filter(([_, value]) => value !== '')
      );
      dispatch(
        updateAuthorizedUserRequest({
          accountId,
          customerId,
          requestBody: filteredRequestBody,
          recaptchaToken: token,
        })
      );
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <div className="bg-[#fafafa] flex flex-col py-4 px-8">
      <Breadcrumbs
        items={[
          {
            name: rb_supplementaryCards?.addSupplementaryCardDetails
              ?.bread_crumbs?.title1,
            href: '/request-supplementary',
          },
          {
            name: rb_supplementaryCards?.addSupplementaryCardDetails
              ?.bread_crumbs?.title2,
            href: '/supplementary-details',
          },
        ]}
      />
      <div className="bg-white min-h-[70vh] px-[27px] py-[29px] rounded-[15px]">
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="h-full"
          data-testid="add-cardholder-form"
        >
          <div className="flex flex-col">
            <h1
              aria-label={
                rb_supplementaryCards?.addSupplementaryCardDetails
                  ?.add_cardHolder_details?.title
              }
              className="text-[22px] font-semibold leading-[28px] tracking-[0.35%] pb-1"
            >
              {renderDescriptionParagraphs(
                rb_supplementaryCards?.addSupplementaryCardDetails
                  ?.add_cardHolder_details?.title,
                ' '
              )}
            </h1>

            <p
              aria-label={`${
                rb_supplementaryCards?.addSupplementaryCardDetails
                  ?.add_cardHolder_details?.description
              } ${productName} ${contactDetails?.availableCards[0]?.cardNumber?.slice(-4)}`}
            >
              {renderDescriptionParagraphs(
                rb_supplementaryCards?.addSupplementaryCardDetails
                  ?.add_cardHolder_details?.description +
                  ' ' +
                  productName +
                  ' ..' +
                  contactDetails?.availableCards[0]?.cardNumber?.slice(-4),
                ' '
              )}
            </p>

            <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mt-10">
              <div className="col-span-1">
                <div className="flex items-center space-x-3">
                  <span className="flex items-center justify-center w-10 h-10 bg-primary-red text-white rounded-full mb-5">
                    1
                  </span>
                  <h3
                    aria-label={
                      rb_supplementaryCards?.addSupplementaryCardDetails
                        ?.add_cardHolder_details?.sub_heading1
                    }
                    className="text-[18px] font-bold mb-4 text-primary-red"
                  >
                    {
                      rb_supplementaryCards?.addSupplementaryCardDetails
                        ?.add_cardHolder_details?.sub_heading1
                    }
                  </h3>
                </div>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-[10px]">
                    <label
                      htmlFor="relationshipToPrimary"
                      aria-label={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.relationship
                      }
                    >
                      {
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.relationship
                      }
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="relationshipToPrimary"
                      className={`h-[48px] py-[12px] px-[15px] border ${
                        errors.relationshipToPrimary
                          ? 'border-primary-red'
                          : 'border-light-gray-border'
                      }`}
                      {...register('relationshipToPrimary')}
                      onBlur={() => trigger('relationshipToPrimary')}
                    >
                      <option
                        value=""
                        className="text-[#666666]"
                        aria-label={
                          rb_supplementaryCards?.addSupplementaryCardDetails
                            ?.placeHolders?.relationship
                        }
                      >
                        {
                          rb_supplementaryCards?.addSupplementaryCardDetails
                            ?.placeHolders?.relationship
                        }
                      </option>
                      {authorizedUserOptions?.configurations
                        ?.find(
                          (config: { name: string }) =>
                            config.name === 'RELATIONSHIP_TO_CUSTOMER'
                        )
                        ?.validOptions?.map((relationship: string) => {
                          const relationshipMap = {
                            COMMON_LAW_HUSBAND:
                              rb_supplementaryCards?.addSupplementaryCardDetails
                                ?.relationshipDropdown?.COMMON_LAW_HUSBAND,
                            COMMON_LAW_WIFE:
                              rb_supplementaryCards?.addSupplementaryCardDetails
                                ?.relationshipDropdown?.COMMON_LAW_WIFE,
                            DAUGHTER:
                              rb_supplementaryCards?.addSupplementaryCardDetails
                                ?.relationshipDropdown?.DAUGHTER,
                            FATHER:
                              rb_supplementaryCards?.addSupplementaryCardDetails
                                ?.relationshipDropdown?.FATHER,
                            HUSBAND:
                              rb_supplementaryCards?.addSupplementaryCardDetails
                                ?.relationshipDropdown?.HUSBAND,
                            MOTHER:
                              rb_supplementaryCards?.addSupplementaryCardDetails
                                ?.relationshipDropdown?.MOTHER,
                            RELATIVE:
                              rb_supplementaryCards?.addSupplementaryCardDetails
                                ?.relationshipDropdown?.RELATIVE,
                            SIGNIFICANT_OTHER:
                              rb_supplementaryCards?.addSupplementaryCardDetails
                                ?.relationshipDropdown?.SIGNIFICANT_OTHER,
                            SON: rb_supplementaryCards
                              ?.addSupplementaryCardDetails
                              ?.relationshipDropdown?.SON,
                            WIFE: rb_supplementaryCards
                              ?.addSupplementaryCardDetails
                              ?.relationshipDropdown?.WIFE,
                          };
                          const description =
                            relationshipMap[
                              relationship as keyof typeof relationshipMap
                            ];
                          return (
                            <option
                              key={relationship}
                              value={relationship}
                              aria-label={description}
                            >
                              {description}
                            </option>
                          );
                        })}
                    </select>
                    {errors.relationshipToPrimary?.message && (
                      <div
                        className="flex items-center gap-1 mt-2"
                        aria-label={`balanceIcon ${errors.relationshipToPrimary?.message}`}
                      >
                        <BalanceIcon fill="#DA291C" width="14" height="14" />
                        <p className={`text-[#DA291C] font-500`}>
                          {errors.relationshipToPrimary?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-[10px]">
                    <label
                      htmlFor="salutation"
                      aria-label={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.salutation
                      }
                    >
                      {
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.salutation
                      }
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="salutation"
                      className={`h-[48px] py-[12px] px-[15px] border ${errors.salutation ? 'border-primary-red' : 'border-light-gray-border'}`}
                      {...register('salutation')}
                      onBlur={() => trigger('salutation')}
                    >
                      <option
                        value=""
                        className="text-[#666666]"
                        aria-label={
                          rb_supplementaryCards?.addSupplementaryCardDetails
                            ?.placeHolders?.salutation
                        }
                      >
                        {
                          rb_supplementaryCards?.addSupplementaryCardDetails
                            ?.placeHolders?.salutation
                        }
                      </option>
                      {authorizedUserOptions?.configurations
                        ?.find(
                          (config: { name: string }) => config.name === 'PREFIX'
                        )
                        ?.validOptions?.map((salutation: string) => {
                          const salutationMap = {
                            MISS: rb_supplementaryCards
                              ?.addSupplementaryCardDetails?.salutationDropdown
                              ?.MISS,
                            MISTER:
                              rb_supplementaryCards?.addSupplementaryCardDetails
                                ?.salutationDropdown?.MISTER,
                            MRS: rb_supplementaryCards
                              ?.addSupplementaryCardDetails?.salutationDropdown
                              ?.MRS,
                            MS: rb_supplementaryCards
                              ?.addSupplementaryCardDetails?.salutationDropdown
                              ?.MS,
                          };
                          const description =
                            salutationMap[
                              salutation as keyof typeof salutationMap
                            ];
                          return (
                            <option
                              key={salutation}
                              value={salutation}
                              aria-label={description}
                            >
                              {description}
                            </option>
                          );
                        })}
                    </select>
                    {errors.salutation?.message && (
                      <div
                        className="flex items-center gap-1 mt-2"
                        aria-label={`balanceIcon ${`balanceIcon ${errors.salutation?.message}`}`}
                      >
                        <BalanceIcon fill="#DA291C" width="14" height="14" />
                        <p className={`text-[#DA291C] font-500`}>
                          {errors.salutation?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-[10px]">
                    <label
                      htmlFor="firstName"
                      aria-label={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.first_name
                      }
                    >
                      {
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.first_name
                      }
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      placeholder={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.placeHolders?.firstName
                      }
                      className={`h-[48px] py-[12px] px-[15px] border ${errors.firstName ? 'border-primary-red' : 'border-light-gray-border'}`}
                      {...register('firstName')}
                      onBlur={() => trigger('firstName')}
                    />
                    {errors.firstName?.message && (
                      <div
                        className="flex items-center gap-1 mt-2"
                        aria-label={`balanceIcon ${errors.firstName?.message}`}
                      >
                        <BalanceIcon fill="#DA291C" width="14" height="14" />
                        <p className={`text-[#DA291C] font-500`}>
                          {errors.firstName?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-[10px]">
                    <label
                      htmlFor="middleInitial"
                      aria-label={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.middle_name
                      }
                    >
                      {
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.middle_name
                      }
                    </label>
                    <input
                      type="text"
                      id="middleInitial"
                      placeholder={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.placeHolders?.middleName
                      }
                      className="h-[48px] py-[12px] px-[15px] border border-light-gray-border"
                      {...register('middleInitial')}
                    />
                    {errors.middleInitial?.message && (
                      <div
                        className="flex items-center gap-1 mt-2"
                        aria-label={`balanceIcon ${errors.middleInitial?.message}`}
                      >
                        <BalanceIcon fill="#DA291C" width="14" height="14" />
                        <p className={`text-[#DA291C] font-500`}>
                          {errors.middleInitial?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-[10px]">
                    <label
                      htmlFor="lastName"
                      aria-label={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.last_name
                      }
                    >
                      {
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.last_name
                      }
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      placeholder={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.placeHolders?.lastName
                      }
                      className={`h-[48px] py-[12px] px-[15px] border ${errors.lastName ? 'border-primary-red' : 'border-light-gray-border'}`}
                      {...register('lastName')}
                      onBlur={() => trigger('lastName')}
                    />
                    {errors.lastName?.message && (
                      <div
                        className="flex items-center gap-1 mt-2"
                        aria-label={`balanceIcon ${errors.lastName?.message}`}
                      >
                        <BalanceIcon fill="#DA291C" width="14" height="14" />
                        <p className={`text-[#DA291C] font-500`}>
                          {errors.lastName?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-[10px]">
                    <label
                      htmlFor="dateOfBirth"
                      aria-label={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.dob
                      }
                    >
                      {
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.dob
                      }
                      <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="dateOfBirth"
                      control={control}
                      render={({ field }) => (
                        <input
                          id="dateOfBirth"
                          placeholder={
                            rb_supplementaryCards?.addSupplementaryCardDetails
                              ?.placeHolders?.dob
                          }
                          className={`h-[48px] py-[12px] px-[15px] border ${errors.dateOfBirth ? 'border-primary-red' : 'border-light-gray-border'}`}
                          {...field}
                          value={dob}
                          onChange={handleDateChange}
                          onBlur={() => trigger('dateOfBirth')}
                        />
                      )}
                    />
                    {errors.dateOfBirth?.message && (
                      <div
                        className="flex items-center gap-1 mt-2"
                        aria-label={`balanceIcon ${errors.dateOfBirth?.message}`}
                      >
                        <BalanceIcon fill="#DA291C" width="14" height="14" />
                        <p className={`text-[#DA291C] font-500`}>
                          {errors.dateOfBirth?.message}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-span-1">
                <div className="flex items-center space-x-3">
                  <span className="flex items-center justify-center w-10 h-10 bg-primary-red text-white rounded-full mb-5">
                    2
                  </span>
                  <h3
                    aria-label={
                      rb_supplementaryCards?.addSupplementaryCardDetails
                        ?.add_cardHolder_details?.sub_heading2
                    }
                    className="text-[18px] font-bold mb-4 text-primary-red"
                  >
                    {
                      rb_supplementaryCards?.addSupplementaryCardDetails
                        ?.add_cardHolder_details?.sub_heading2
                    }
                  </h3>
                </div>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-[10px]">
                    <label
                      htmlFor="addressLine1"
                      aria-label={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.address_line1
                      }
                    >
                      {
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.address_line1
                      }
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="addressLine1"
                      placeholder={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.placeHolders?.address
                      }
                      className={`h-[48px] py-[12px] px-[15px] border ${errors.addressLine1 ? 'border-primary-red' : 'border-light-gray-border'}`}
                      {...register('addressLine1')}
                      onBlur={() => trigger('addressLine1')}
                    />
                    {errors.addressLine1?.message && (
                      <div
                        className="flex items-center gap-1 mt-2"
                        aria-label={`balanceIcon ${errors.addressLine1?.message}`}
                      >
                        <BalanceIcon fill="#DA291C" width="14" height="14" />
                        <p className={`text-[#DA291C] font-500`}>
                          {errors.addressLine1?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-[10px]">
                    <label
                      htmlFor="addressLine2"
                      aria-label={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.address_line2
                      }
                    >
                      {
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.address_line2
                      }
                    </label>
                    <input
                      type="text"
                      id="addressLine2"
                      placeholder={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.placeHolders?.address
                      }
                      className="h-[48px] py-[12px] px-[15px] border border-light-gray-border"
                      {...register('addressLine2')}
                    />
                    {errors.addressLine2?.message && (
                      <div
                        className="flex items-center gap-1 mt-2"
                        aria-label={`balanceIcon ${errors.addressLine2?.message}`}
                      >
                        <BalanceIcon fill="#DA291C" width="14" height="14" />
                        <p className={`text-[#DA291C] font-500`}>
                          {errors.addressLine2?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-[10px]">
                    <label
                      htmlFor="country"
                      aria-label={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.country
                      }
                    >
                      {
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.country
                      }
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="country"
                      value="Canada"
                      readOnly // Prevent editing
                      className="h-[48px] py-[12px] px-[15px] border border-light-gray-border"
                    />
                  </div>

                  <div className="flex flex-col gap-[10px]">
                    <label
                      htmlFor="province"
                      aria-label={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.province
                      }
                    >
                      {
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.province
                      }
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="province"
                      className={`h-[48px] py-[12px] px-[15px] border ${errors.province ? 'border-primary-red' : 'border-light-gray-border'}`}
                      {...register('province')}
                      onBlur={() => trigger('province')}
                    >
                      <option
                        value=""
                        className="text-[#666666]"
                        aria-label={
                          rb_supplementaryCards?.addSupplementaryCardDetails
                            ?.placeHolders?.province
                        }
                      >
                        {
                          rb_supplementaryCards?.addSupplementaryCardDetails
                            ?.placeHolders?.province
                        }
                      </option>
                      {rb_supplementaryCards?.addSupplementaryCardDetails
                        ?.provinceDropdown &&
                        Object.entries(
                          rb_supplementaryCards?.addSupplementaryCardDetails
                            ?.provinceDropdown as Record<string, string>
                        ).map(([key, value]) => (
                          <option
                            key={key}
                            value={provinceCodeMap[key] || key}
                            aria-label={value}
                          >
                            {value}
                          </option>
                        ))}
                    </select>
                    {errors.province?.message && (
                      <div
                        className="flex items-center gap-1 mt-2"
                        aria-label={`balanceIcon ${errors.province?.message}`}
                      >
                        <BalanceIcon fill="#DA291C" width="14" height="14" />
                        <p className={`text-[#DA291C] font-500`}>
                          {errors.province?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-[10px]">
                    <label
                      htmlFor="city"
                      aria-label={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.city
                      }
                    >
                      {
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.city
                      }
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="city"
                      placeholder={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.placeHolders?.city
                      }
                      className={`h-[48px] py-[12px] px-[15px] border ${errors.city ? 'border-primary-red' : 'border-light-gray-border'}`}
                      {...register('city')}
                      onBlur={() => trigger('city')}
                    />
                    {errors.city?.message && (
                      <div
                        className="flex items-center gap-1 mt-2"
                        aria-label={`balanceIcon ${errors.city?.message}`}
                      >
                        <BalanceIcon fill="#DA291C" width="14" height="14" />
                        <p className={`text-[#DA291C] font-500`}>
                          {errors.city?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-[10px]">
                    <label
                      htmlFor="postalCode"
                      aria-label={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.postal_code
                      }
                    >
                      {
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.labels?.postal_code
                      }
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="postalCode"
                      placeholder={
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          ?.placeHolders?.postalCode
                      }
                      className={`h-[48px] py-[12px] px-[15px] border ${errors.postalCode ? 'border-primary-red' : 'border-light-gray-border'}`}
                      {...register('postalCode')}
                      onBlur={() => trigger('postalCode')}
                    />
                    {errors.postalCode?.message && (
                      <div
                        className="flex items-center gap-1 mt-2"
                        aria-label={`balanceIcon ${errors.postalCode?.message}`}
                      >
                        <BalanceIcon fill="#DA291C" width="14" height="14" />
                        <p className={`text-[#DA291C] font-500`}>
                          {errors.postalCode?.message}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1 md:col-span-2">
                <div className="flex items-center space-x-3">
                  <span className="flex items-center justify-center w-10 h-10 bg-primary-red text-white rounded-full mb-5">
                    3
                  </span>
                  <h3
                    aria-label={
                      rb_supplementaryCards?.addSupplementaryCardDetails
                        ?.add_cardHolder_details?.sub_heading3
                    }
                    className="text-[18px] font-bold mb-4 text-primary-red"
                  >
                    {
                      rb_supplementaryCards?.addSupplementaryCardDetails
                        ?.add_cardHolder_details?.sub_heading3
                    }
                  </h3>
                </div>
                <div className="lg:flex lg:flex-col gap-4 md:grid md:grid-cols-2">
                  {phoneFields.map((field) => (
                    <div className="flex flex-col gap-[10px]" key={field}>
                      <label htmlFor={field} aria-label={phoneLabels[field]}>
                        {phoneLabels[field]}
                        {field === 'mobile' && (
                          <span className="text-red-500">*</span>
                        )}
                      </label>
                      <Controller
                        name={field === 'mobile' ? 'mobile' : `${field}`}
                        control={control}
                        render={({ field: controllerField }) => (
                          <input
                            type="text"
                            id={field}
                            className={`h-[48px] py-[12px] px-[15px] border ${errors[`${field}`] ? 'border-primary-red' : 'border-light-gray-border'}`}
                            placeholder={phonePlaceholders[field]}
                            {...controllerField}
                            value={phoneNumbers[field]}
                            onChange={(e) => handlePhoneChange(e, field)}
                            onBlur={() => trigger('mobile')}
                          />
                        )}
                      />
                      {field === 'mobile' && errors.mobile?.message && (
                        <div
                          className="flex items-center gap-1 mt-2"
                          aria-label={`balanceIcon ${errors.mobile?.message}`}
                        >
                          <BalanceIcon fill="#DA291C" width="14" height="14" />
                          <p className={`text-[#DA291C] font-500`}>
                            {errors.mobile?.message}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full mt-5 border-solid border-0 border-t border-[#E9E9E9] flex flex-col md:flex-row md:items-center justify-between">
            <p
              aria-label={
                rb_supplementaryCards?.addSupplementaryCardDetails?.conditions
                  ?.section1
              }
              className="mt-7 text-[#1F1F1F] text-[16px] font-[500] leading-[20px] tracking-[-0.051px] md:mr-6"
            >
              {
                rb_supplementaryCards?.addSupplementaryCardDetails?.conditions
                  ?.section1
              }{' '}
              <a
                className="text-[#DA291C] cursor-pointer"
                onClick={() =>
                  window.open(
                    rb_links?.rbLinksCollection
                      ?.manage_Card_Request_sup_card_pdf,
                    '_blank'
                  )
                }
                aria-label={
                  rb_supplementaryCards?.addSupplementaryCardDetails?.conditions
                    ?.section2
                }
              >
                {
                  rb_supplementaryCards?.addSupplementaryCardDetails?.conditions
                    ?.section2
                }
              </a>{' '}
              {
                rb_supplementaryCards?.addSupplementaryCardDetails?.conditions
                  ?.section3
              }
            </p>
            <div className="flex flex-row gap-4 sm:w-[200px] md:gap-6 mt-5 md:mt-7 md:w-[450px]">
              <button
                aria-label={
                  rb_supplementaryCards?.addSupplementaryCardDetails?.cancelCta
                }
                type="button"
                onClick={() => navigate('/request-supplementary')}
                className="text-center flex-1 items-center align-center border border-solid border-[#DA291C] text-[#DA291C] px-[20px] py-[12px] bg-[#fff]"
              >
                {rb_supplementaryCards?.addSupplementaryCardDetails?.cancelCta}
              </button>
              <button
                aria-label={
                  rb_supplementaryCards?.addSupplementaryCardDetails?.submitCta
                }
                type="submit"
                className={`text-center flex-1 items-center align-center border border-solid px-[20px] py-[12px] ${isValid ? 'border-[#DA291C] text-[#ffffff] bg-[#DA291C]' : 'border-[#D9D9D9] text-[#999999] bg-[#F5F5F5]'}`}
                disabled={!(isValid && isDirty)}
              >
                {rb_supplementaryCards?.addSupplementaryCardDetails?.submitCta}
              </button>
            </div>
          </div>
          {loadingAuthUser && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center z-10">
              <Spinner />
            </div>
          )}
          <Modal
            isOpen={isSuccessModalOpen}
            contentLabel="supplementary card details"
            className="fixed p-0 w-[330px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 md:outline-none"
            overlayClassName="fixed inset-0 bg-black bg-opacity-50"
          >
            <div className="flex flex-col justify-center items-center gap-[20px] p-[20px] rounded-lg bg-white overflow-auto relative">
              <img
                src={assets?.device_recogonized_success_popup_icon}
                alt="rightIcon"
                className="w-20 h-20"
              />
              <h3
                aria-label={
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.success_modal?.header
                }
                className="text-[#1F1F1F] text-[22px] font-[650] leading-[28px] tracking-[0.077px] text-center"
              >
                {
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.success_modal?.header
                }
              </h3>
              <p
                aria-label={
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.success_modal?.description1
                }
                className="text-[#1F1F1F] text-[16px] font-[500] leading-[20px] tracking-[-0.051px] text-center"
              >
                {
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.success_modal?.description1
                }
              </p>
              <p
                aria-label={
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.success_modal?.description2
                }
                className="text-[#1F1F1F] text-[16px] font-[500] leading-[20px] tracking-[-0.051px] text-center"
              >
                {
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.success_modal?.description2
                }
              </p>
              <p
                aria-label={`${
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.success_modal?.confirmation
                } ${confirmationNumber}`}
                className="text-[#1F1F1F] text-[16px] font-[500] leading-[20px] tracking-[-0.051px] text-center"
              >
                {
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.success_modal?.confirmation
                }{' '}
                {confirmationNumber}
              </p>
              <button
                aria-label={
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.success_modal?.cta
                }
                type="button"
                onClick={() => {
                  navigate('/request-supplementary', {
                    state: { refresh: true },
                  });
                  dispatch(resetUpdateAuthorizedUser());
                }}
                className="text-center flex-1 items-center align-center text-white px-[20px] py-[12px] bg-primary-red"
              >
                {
                  rb_supplementaryCards?.addSupplementaryCardDetails
                    ?.success_modal?.cta
                }
              </button>
            </div>
          </Modal>
          {(failedModalOpen || isRecaptchaModalOpen) && (
            <CommanModal
              isOpen={isSuccessFailed || isRecaptchaModalOpen}
              jsxContent={
                <>
                  <div
                    aria-label={
                      isRecaptchaModalOpen
                        ? recaptchaError?.title
                        : (rb_error.supplementaryCards[
                            errorOptions?.title
                              ?.toUpperCase()
                              ?.replace(/\s+/g, '_')
                          ]?.title ??
                          errorOptions?.title ??
                          rb_supplementaryCards?.addSupplementaryCardDetails
                            .temporary_unavailable.title)
                    }
                    className="mt-[20px] text-lg font-semibold text-center"
                  >
                    {isRecaptchaModalOpen
                      ? recaptchaError?.title
                      : (rb_error.supplementaryCards[
                          errorOptions?.title
                            ?.toUpperCase()
                            ?.replace(/\s+/g, '_')
                        ]?.title ??
                        errorOptions?.title ??
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          .temporary_unavailable.title)}
                  </div>
                  <div
                    className="mt-[20px]"
                    aria-label={
                      isRecaptchaModalOpen
                        ? recaptchaError?.description
                        : (rb_error.supplementaryCards[
                            errorOptions?.title
                              ?.toUpperCase()
                              ?.replace(/\s+/g, '_')
                          ]?.detail ??
                          errorOptions?.detail ??
                          rb_supplementaryCards?.addSupplementaryCardDetails
                            .temporary_unavailable.description)
                    }
                  >
                    {isRecaptchaModalOpen
                      ? recaptchaError?.description
                      : (rb_error.supplementaryCards[
                          errorOptions?.title
                            ?.toUpperCase()
                            ?.replace(/\s+/g, '_')
                        ]?.detail ??
                        errorOptions?.detail ??
                        rb_supplementaryCards?.addSupplementaryCardDetails
                          .temporary_unavailable.description)}
                  </div>
                </>
              }
              onClose={() => {
                navigate('/request-supplementary');
                dispatch(resetUpdateAuthorizedUser());
              }}
              buttonText={
                rb_supplementaryCards?.addSupplementaryCardDetails?.okCta
              }
              cardStatus={true}
              closeIcon={false}
            />
          )}
        </form>
      </div>
    </div>
  );
};

export default AddCardHolderForm;
